/**
 * tableEdit — pure helpers for the table-aware "edit highlight" flow.
 *
 * When the writer highlights cells in a Word table and asks for an edit,
 * each affected ROW is rewritten one at a time by the same engine a
 * normal highlight edit uses (see useHighlightEditFlow), then the results
 * are assembled into a single snippet-diff preview with the changed cells
 * highlighted. This module owns the pure text layer of that flow:
 *
 *   - render a row's cells as a single markdown pipe row (what we send to
 *     the rewrite engine as ``selected_text``, and what we replace in the
 *     SSOT markdown draft),
 *   - parse the model's returned pipe row back into cells,
 *   - diff old vs new cells to compute which cells changed.
 *
 * All Office.js / Word interaction lives in wordWriter (captureTableSelection,
 * applyTableRowEditsInSection); everything here is host-free and unit-tested.
 */

/** One cell in the snippet diff: its original and proposed text, and
 *  whether the two differ (whitespace-normalized). */
export interface TableCellDiff {
  old: string;
  new: string;
  changed: boolean;
}

/** One row in the snippet diff, keyed by its 0-based index in the Word
 *  table (global, header-inclusive — the same index ``getCell`` uses). */
export interface TableRowDiff {
  rowIndex: number;
  /** Per-cell diff, aligned to the table's columns. Empty when the row
   *  was skipped (see ``skippedReason``). */
  cells: TableCellDiff[];
  /** Set when the row could not be applied — e.g. the model returned a
   *  row with a different column count. The row is shown unchanged and
   *  excluded from the apply. */
  skippedReason?: string;
}

/** The assembled proposal model handed to the preview + apply. */
export interface TableEditSnippet {
  /** Header row cells (table row 0), for the preview's column headings.
   *  Null when the highlighted rows include row 0 (it's shown as data). */
  header: string[] | null;
  rows: TableRowDiff[];
  /** Total cells that changed across all non-skipped rows. */
  changedCells: number;
  /** Rows skipped because the rewrite came back malformed. */
  skippedRows: number;
}

const norm = (s: string): string => s.replace(/\s+/g, " ").trim();

/** Render a row's cells as a single markdown pipe row: ``| a | b | c |``.
 *  Used both as the ``selected_text`` sent to the rewrite engine and as
 *  the needle/replacement for the SSOT markdown draft. */
export function rowToPipe(cells: readonly string[]): string {
  return `| ${cells.map((c) => norm(c).replace(/\|/g, "\\|")).join(" | ")} |`;
}

/** Parse a single markdown pipe row back into trimmed cell strings.
 *  Returns null when ``line`` isn't a single pipe row (no leading/trailing
 *  pipe, a separator row like ``|---|``, or multiple lines). The model is
 *  asked to return exactly one row; anything else is treated as malformed
 *  so the caller can skip that row rather than guess cell alignment. */
export function parsePipeRow(line: string): string[] | null {
  const trimmedInput = line.trim();
  if (!trimmedInput || trimmedInput.includes("\n")) return null;
  const m = /^\|(.*)\|$/.exec(trimmedInput);
  if (!m) return null;
  // A separator row (|---|:--:|) carries no data — reject it.
  if (/^\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*$/.test(m[1])) return null;
  // Split on unescaped pipes, then unescape.
  const cells = m[1]
    .split(/(?<!\\)\|/)
    .map((c) => c.replace(/\\\|/g, "|").trim());
  return cells;
}

/** Diff a row's original cells against the model's returned cells.
 *  Returns null when the column counts differ — the caller skips the row
 *  (never realign/pad, which would silently corrupt data). */
export function diffRow(oldCells: readonly string[], newCells: readonly string[]): TableCellDiff[] | null {
  if (oldCells.length !== newCells.length) return null;
  return oldCells.map((oldCell, i) => {
    const newCell = newCells[i];
    return { old: oldCell, new: newCell, changed: norm(oldCell) !== norm(newCell) };
  });
}

/** True when at least one cell in the diff changed. */
export function rowHasChange(cells: readonly TableCellDiff[]): boolean {
  return cells.some((c) => c.changed);
}

/** True when a diffed row would blank a row that had content: every
 *  proposed cell is empty, yet at least one original cell was non-empty.
 *  Such a rewrite is treated as malformed (a data-loss vector) and skipped,
 *  never applied. */
export function isBlankingRow(cells: readonly TableCellDiff[]): boolean {
  return cells.every((c) => norm(c.new) === "") && cells.some((c) => norm(c.old) !== "");
}

/** Build the proposal snippet from the per-row rewrite results. Each entry
 *  pairs the original captured cells with the model's returned row text
 *  (null when the rewrite failed/produced no row). Rows with a column-count
 *  mismatch or missing result are marked skipped and left unchanged. */
export function buildSnippet(
  results: ReadonlyArray<{ rowIndex: number; oldCells: string[]; rawReplacement: string | null }>,
  header: string[] | null,
): TableEditSnippet {
  let changedCells = 0;
  let skippedRows = 0;
  const rows: TableRowDiff[] = results.map(({ rowIndex, oldCells, rawReplacement }) => {
    const parsed = rawReplacement != null ? parsePipeRow(rawReplacement) : null;
    const diff = parsed ? diffRow(oldCells, parsed) : null;
    if (!diff) {
      skippedRows += 1;
      return {
        rowIndex,
        cells: oldCells.map((c) => ({ old: c, new: c, changed: false })),
        skippedReason:
          rawReplacement == null ? "the rewrite produced no result" : "the rewrite changed the number of columns",
      };
    }
    // Guard: a rewrite that empties every cell of a non-empty row is
    // treated as malformed and skipped — never applied (data loss).
    if (isBlankingRow(diff)) {
      skippedRows += 1;
      return {
        rowIndex,
        cells: oldCells.map((c) => ({ old: c, new: c, changed: false })),
        skippedReason: "the rewrite emptied the row",
      };
    }
    changedCells += diff.filter((c) => c.changed).length;
    return { rowIndex, cells: diff };
  });
  return { header, rows, changedCells, skippedRows };
}

/** The rows + new cell values to apply to Word, drawn from a snippet.
 *  Only rows with at least one changed cell are included. */
export interface TableRowEdit {
  rowIndex: number;
  /** Full row of proposed cell values (new text), aligned to columns. */
  cells: string[];
  /** Column indices whose text changed — the only cells actually written. */
  changedColumns: number[];
}

/** Extract the applicable edits (changed rows only) from a snippet. */
export function editsFromSnippet(snippet: TableEditSnippet): TableRowEdit[] {
  const edits: TableRowEdit[] = [];
  for (const row of snippet.rows) {
    if (row.skippedReason || !rowHasChange(row.cells)) continue;
    edits.push({
      rowIndex: row.rowIndex,
      cells: row.cells.map((c) => c.new),
      changedColumns: row.cells.map((c, i) => (c.changed ? i : -1)).filter((i) => i >= 0),
    });
  }
  return edits;
}
